import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const nassauTowns = [
  {
    name: 'Old Westbury',
    slug: 'old-westbury-ny',
    description: 'Serving historic estates and luxury properties throughout Old Westbury, Reflections Cleanings provides high-end house washing, roof cleaning, window cleaning, and gutter maintenance tailored to Gold Coast homes. Large estates, gated properties, and custom architecture require a professional approach — and we deliver results that truly reflect the quality of your home.'
  },
  {
    name: 'Brookville',
    slug: 'brookville-ny',
    description: 'Brookville is known for expansive private estates and heavily wooded properties. Moisture and tree coverage often lead to algae and organic buildup on siding and roofing systems. Reflections Cleanings specializes in safe soft washing solutions that preserve natural materials while restoring their original appearance.'
  },
  {
    name: 'Muttontown',
    slug: 'muttontown-ny',
    description: 'Luxury homes in Muttontown are surrounded by mature landscaping and natural elements that require consistent exterior maintenance. We provide professional roof washing, cedar cleaning, and estate window cleaning to ensure your property reflects its full beauty year-round.'
  },
  {
    name: 'Sands Point',
    slug: 'sands-point-ny',
    description: 'Waterfront homes in Sands Point face salt exposure, moisture, and environmental buildup. Reflections Cleanings offers specialized exterior cleaning services designed for coastal conditions, restoring clarity to windows and protecting siding from salt-related staining.'
  },
  {
    name: 'Oyster Bay Cove',
    slug: 'oyster-bay-cove-ny',
    description: 'Oyster Bay Cove features historic and architecturally significant homes that demand careful, knowledgeable cleaning methods. Our soft washing systems remove algae and discoloration without damaging delicate materials, preserving the elegance of North Shore properties.'
  },
  {
    name: 'Plandome',
    slug: 'plandome-ny',
    description: 'Plandome homeowners take pride in beautifully maintained properties with manicured landscaping and detailed exteriors. Reflections Cleanings provides streak-free window cleaning, roof treatments, and exterior washing services that elevate curb appeal and protect long-term value.'
  },
  {
    name: 'Hewlett Bay Park',
    slug: 'hewlett-bay-park-ny',
    description: 'Hewlett Bay Park offers luxury waterfront and South Shore homes that require consistent exterior upkeep. From roof cleaning to soft washing and precision window care, Reflections Cleanings ensures your home reflects sophistication and care.'
  }
];

const NassauCountyPage = () => {
  const goToQuote = () => {
    window.location.href = '/#quote';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 relative">
        <div 
          className="w-full h-[500px] bg-cover bg-center relative"
          style={{ 
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/49224efbf4fdc02d1e9ecfc12e0ae04539802c061123be079450b3ad58ec7dda.png')` 
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl text-white">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Luxury Exterior Cleaning Services in Nassau County, NY
                </h1>
                <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
                  <span className="text-gradient-reflections font-bold">Reflections Cleanings</span> proudly provides professional exterior cleaning services 
                  throughout Nassau County's most prestigious North Shore and South Shore communities.
                  From historic Gold Coast estates to waterfront luxury homes, we specialize in preserving 
                  high-end properties with meticulous soft washing, roof cleaning, window cleaning, and gutter maintenance.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="tel:631-660-8164" 
                    className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    (631) 660-8164
                  </a>
                  <button
                    onClick={goToQuote}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                  >
                    Request a Free Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Nassau County is home to some of New York's most distinguished residential communities. 
            Luxury estates, waterfront properties, and historic homes require an experienced, 
            detail-oriented exterior cleaning company.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>, 
            we understand the materials, architecture, and environmental conditions unique to Nassau County. 
            From cedar shake siding and slate roofs to large glass installations and custom stonework, 
            our soft washing and cleaning systems are designed to restore beauty while protecting delicate surfaces.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed font-medium">
            Our name represents our standard — your home should reflect the pride and care you put into it.
          </p>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">
            Our Nassau County Service Areas
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Below are the primary Nassau County communities we serve:
          </p>

          <div className="space-y-8">
            {nassauTowns.map((town, index) => (
              <div 
                key={town.slug}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 sm:p-8"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin className="w-6 h-6 text-blue-600" />
                      <h3 className="text-2xl font-bold text-gray-900">{town.name}, NY</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {town.description}
                    </p>
                    <Link
                      to={`/${town.slug}`}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                    >
                      Learn more about our services in {town.name}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Nassau County Homeowners Choose <span className="text-gradient-reflections">Reflections Cleanings</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Fully insured exterior cleaning specialists',
              'Experienced with luxury and estate-level properties',
              'Soft washing experts protecting delicate materials',
              'Discreet, professional, and respectful service',
              'Trusted throughout Long Island and the Hamptons',
              'We don\'t just clean homes — we restore the reflection of your investment'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Request a Free Nassau County Exterior Cleaning Quote
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            If you own a luxury home in Nassau County, trust <span className="text-gradient-reflections font-bold">Reflections Cleanings</span> 
            to preserve its beauty and value.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:631-660-8164" 
              className="flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors text-lg"
            >
              <Phone className="w-5 h-5" />
              Call (631) 660-8164
            </a>
            <button
              onClick={goToQuote}
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold transition-colors text-lg"
            >
              Schedule Your Free Quote Today
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default NassauCountyPage;
